import { User } from '@supabase/supabase-js';

import { ApiResponse_GetAll_PropertyLocation } from '@/api/endpoints/propertyLocation/useGetAllPropertyLocations';
import { ApiResponse_GetAllInBuilding_Unit } from '@/api/endpoints/unit/useGetAllUnitsInBuilding';
import { OnboardingProgressWithAccountTypeModel } from '@/api/models/propertyManager';
import { OnboardingRoutes, PMPRoutes } from '@/config/routes';

import { Option } from '@/components/form/FormSelect';

import { getResolvedRoutePath } from './router';

export type ChecklistItem = {
  title: string;
  description: string;
  isCompleted: boolean;
  isOptional?: boolean;
  actionLabel: string;
  routeOptions?: Option[];
  route: string;
  isDisabled?: boolean;
  benefits?: string[];
  completedTitle: string;
  letsTitle: string;
  letsDescription: string;
};

export const getChecklistItems = ({
  isSingleFamilyLocation,
  onboardingProgress,
  selectedPropertyLocation,
  user,
  units = [],
}: {
  isSingleFamilyLocation: boolean;
  onboardingProgress: OnboardingProgressWithAccountTypeModel;
  selectedPropertyLocation: ApiResponse_GetAll_PropertyLocation[0] | null;
  user: User | null;
  units: ApiResponse_GetAllInBuilding_Unit;
}): ChecklistItem[] => [
  {
    title: 'Complete personal details',
    description: 'Complete your profile.',
    isCompleted: !!user?.user_metadata.full_name,
    actionLabel: 'Update Profile',
    route: PMPRoutes.account,
    benefits: [
      'Personalize your experience.',
      'Help us tailor recommendations to your needs.',
      "Allow tenants to know who they're working with.",
    ],
    completedTitle: 'completed personal details',
    letsTitle: 'complete your personal details',
    letsDescription: 'Completing your profile',
  },
  {
    title: 'Find your property',
    description: 'Add your property details.',
    isCompleted: !!onboardingProgress.hasPropertyLocation,
    actionLabel: 'Add Property',
    isOptional: true,
    route: OnboardingRoutes.propertyLocationCreate,
    benefits: [
      'Organize your rental portfolio.',
      'Access property-specific insights and tools.',
      'Enable location-based features and reporting.',
    ],
    completedTitle: 'found your property',
    letsTitle: 'find your property',
    letsDescription: 'Adding your property details',
  },
  ...(!isSingleFamilyLocation
    ? [
      {
        title: 'Add building',
        description:
            'Add your building details to manage your property effectively. This helps organize your rental properties.',
        isCompleted: !!onboardingProgress.hasBuilding,
        actionLabel: 'Add Building',
        route: `${getResolvedRoutePath(PMPRoutes.propertyLocationViewBuildingList, {
          id: selectedPropertyLocation?.id?.toString() ?? '',
        })}?openAddBuilding=true`,
        isDisabled: !!onboardingProgress.hasBuilding || !selectedPropertyLocation?.id,
        isOptional: true,
        benefits: [
          'Organize multi-unit properties efficiently.',
          'Group units for better management.',
          'Enable building-specific insights and reports.',
        ],
        completedTitle: 'added building details',
        letsTitle: 'add building details',
        letsDescription: 'Adding building details',
      },
      {
        title: 'Add unit that tenants live in',
        description:
            'Add units in your building to manage your tenants effectively. This helps track tenant occupancy.',
        isCompleted: !!onboardingProgress.hasUnit,
        actionLabel: 'Add Unit',
        isOptional: true,
        route: `${getResolvedRoutePath(PMPRoutes.buildingViewUnitList, {
          id: onboardingProgress.firstBuildingId?.toString() ?? '',
        })}?openAddUnit=true`,
        isDisabled: !!onboardingProgress.hasUnit || !onboardingProgress.firstBuildingId,
        benefits: [
          'Track individual rental units separately.',
          'Assign tenants to specific units.',
          'Monitor unit-specific maintenance and financials.',
        ],
        completedTitle: 'added unit details',
        letsTitle: 'add unit details',
        letsDescription: 'Adding unit details',
      },
    ]
    : []),
  {
    title: 'Add tenant details',
    description: 'Add tenant details to manage your property effectively.',
    isCompleted: !!onboardingProgress.hasTenant,
    actionLabel: 'Add Tenants',
    isOptional: true,
    route: `${getResolvedRoutePath(PMPRoutes.unitViewTenantList, {
      id: onboardingProgress.firstUnitId?.toString() ?? '',
    })}?openAddTenant=true`,
    isDisabled: !!onboardingProgress.hasTenant || !onboardingProgress.firstUnitId,
    benefits: [
      'Track tenant information in one place.',
      'Streamline communication with tenants.',
      'Enable rent collection and maintenance requests.',
    ],
    completedTitle: 'added tenant details',
    letsTitle: 'add tenant details',
    letsDescription: 'Adding tenant details',
  },
  {
    title: 'Pick a virtual property phone number',
    description:
      'Tenants, vendors, and other contacts will use this as the main phone number for your property.',
    isCompleted: !!onboardingProgress.hasPhoneNumber,
    actionLabel: 'Get Phone Number',
    isOptional: true,
    route: getResolvedRoutePath(PMPRoutes.propertyLocationPhoneCreate, {
      id: selectedPropertyLocation?.id?.toString() ?? '',
    }),
    isDisabled: !selectedPropertyLocation?.id || !onboardingProgress.hasTenant,
    benefits: [
      'Obtain a virtual dedicated property contact line.',
      'Keep personal and business communications separate.',
      'Provide professional contact information to tenants and vendors without sharing your personal phone number.',
    ],
    completedTitle: 'got property phone number',
    letsTitle: 'get property phone number',
    letsDescription: 'Getting property phone number',
  },
  {
    title: 'Send tenant onboarding email',
    description:
      'Invite your tenant to set up their account and access the portal. This allows them to pay their rent and look at their communication history.',
    isCompleted: !!onboardingProgress.hasTenantsWithOnboardingEmailSent,
    isOptional: true,
    actionLabel: 'Send Email',
    route: `${getResolvedRoutePath(PMPRoutes.propertyLocationView, {
      id: selectedPropertyLocation?.id?.toString() ?? '',
    })}?openInviteTenant=true`,
    isDisabled: !onboardingProgress.hasTenant,
    benefits: [
      'Enable tenant self-service features.',
      'Simplify rent collection and maintenance requests.',
      'Establish clear communication channels.',
    ],
    completedTitle: 'sent tenant onboarding email',
    letsTitle: 'send tenant onboarding email',
    letsDescription: 'Sending tenant onboarding email',
  },
  ...(isSingleFamilyLocation
    ? [
      {
        title: 'Upload lease document',
        description: 'Upload the lease agreement for your tenant.',
        isCompleted: !!onboardingProgress.hasLeaseDocument,
        actionLabel: 'Upload Lease',
        isOptional: true,
        route: `${PMPRoutes.vault}?openDocumentUpload=true&defaultCategoryId=2`,
        isDisabled: !onboardingProgress.hasTenant,
        benefits: [
          'Securely store important lease documents.',
          'Easily reference terms and conditions.',
          'Keep a digital record of all agreements.',
        ],
        completedTitle: 'uploaded lease document',
        letsTitle: 'upload lease document',
        letsDescription: 'Uploading lease document',
      },
    ]
    : []),
  {
    title: 'Connect Stripe',
    description: 'Connect your Stripe account to use rent payment feature.',
    isCompleted: !!onboardingProgress.hasStripeConnected,
    isOptional: true,
    actionLabel: 'Connect Stripe',
    route: PMPRoutes.accountPayouts,
    isDisabled: !onboardingProgress.hasLeaseDocument,
    benefits: [
      'Simplify rent collection and recurring ACH transfers.',
      'Ensure secure and convenient payment options for tenants.',
      'Streamline financial management for property owners.',
    ],
    completedTitle: 'connected Stripe',
    letsTitle: 'connect Stripe',
    letsDescription: 'Connecting Stripe',
  },
  ...(onboardingProgress.hasStripeConnected && units.length > 0 && onboardingProgress.firstUnitId
    ? [
      {
        title: 'Manage Rent',
        description: 'Manage rent for units in your property if you have Stripe connected.',
        isCompleted: false,
        isOptional: true,
        actionLabel: 'Manage Rent',
        routeOptions: units.map(unit => ({
          label: `Unit: ${unit.name}`,
          value: getResolvedRoutePath(PMPRoutes.unitViewRent, {
            id: unit.id.toString(),
          }),
        })),
        route: getResolvedRoutePath(PMPRoutes.unitViewRent, {
          id: onboardingProgress.firstUnitId.toString(),
        }),
        isDisabled: !onboardingProgress.hasStripeConnected,
        benefits: [],
        completedTitle: 'managed rent',
        letsTitle: 'manage rent',
        letsDescription: 'Managing rent',
      },
    ]
    : []),
];

export const getIncompleteChecklistItems = (checklistItems: ChecklistItem[]) =>
  checklistItems.filter(item => !item.isCompleted);

export const getCompletedChecklistItems = (checklistItems: ChecklistItem[]) =>
  checklistItems.filter(item => !!item.isCompleted);

export const getLastCompletedItem = (checklistItems: ChecklistItem[]) => {
  const completedItems = getCompletedChecklistItems(checklistItems);
  return completedItems[completedItems.length - 1];
};

export const getNextItem = (checklistItems: ChecklistItem[]): ChecklistItem | undefined => {
  const incompleteItems = getIncompleteChecklistItems(checklistItems);
  return incompleteItems[0];
};

export const getNextMandatoryItem = (checklistItems: ChecklistItem[]) =>
  getIncompleteChecklistItems(checklistItems).find(item => !item.isOptional);

export const getNextOptionalItem = (checklistItems: ChecklistItem[]) =>
  getIncompleteChecklistItems(checklistItems).find(item => !!item.isOptional);

export const getCompletionPercentage = (checklistItems: ChecklistItem[]) => {
  // Filter out optional items
  const mandatoryItems = checklistItems.filter(item => !item.isOptional);
  const incompleteMandatoryItems = mandatoryItems.filter(item => !item.isCompleted);

  // If there are no mandatory items, return 0
  if (mandatoryItems.length === 0) return 0;

  return Math.round(
    ((mandatoryItems.length - incompleteMandatoryItems.length) / mandatoryItems.length) * 100
  );
};
